import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NotificationService } from '../services/notification.service';
import { ConfirmationService } from 'primeng/api';
import { Router } from '@angular/router';

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const notificationService = inject(NotificationService);
  const confirmationService = inject(ConfirmationService);
  const router = inject(Router);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      document.body.style.overflow = 'auto';
      let errorMessage = '';

      // Handle Joi validation errors (error.error.details array)
      if (error?.error?.error?.details && Array.isArray(error.error.error.details)) {
        const errorDetails = error.error.error.details;
        if (errorDetails.length > 0) {
          // Use the server's error message directly
          const firstError = errorDetails[0];
          errorMessage = firstError.message || 'Validation error occurred';
        }
      } else if (error?.error?.details && Array.isArray(error.error.details)) {
        // Handle case where error.details is directly in error.error
        const errorDetails = error.error.details;
        if (errorDetails.length > 0) {
          const firstError = errorDetails[0];
          errorMessage = firstError.message || 'Validation error occurred';
        }
      } else if (error?.error?.error?.message) {
        errorMessage = error.error.error.message;
      } else if (error?.error?.message) {
        errorMessage = error.error.message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else {
        errorMessage = 'Unexpected error occurred';
      }

      // Only hard-block on primary page GETs — secondary loads (vendors/groups
      // for report filters, etc.) must not kick the user to /no-access.
      if (
        error.status === 403 &&
        req.method === 'GET' &&
        !/\/api\/(vendors|subgroups|facilities|printers|api-scopes)(\?|$)/.test(req.url)
      ) {
        router.navigateByUrl('/no-access');
      } else if (error.status === 406) {
        confirmationService.confirm({
          header: `SESSION EXPIRED`,
          message: `Your session has been expired. Please re-login to continue!`,
          icon: 'pi pi-exclamation-circle',
          acceptLabel: 'Login',
          acceptIcon: 'pi',
          acceptButtonStyleClass: 'p-button-primary',
          rejectVisible: false,
          accept: () => {
            localStorage.clear();
            window.location.href = '/login';
          },
        });
      } else if (error.status === 402) {
        if (window.location.pathname !== '/accept-invitation') {
          // Use the actual error message from backend for subscription errors
          const subscriptionErrorMessage =
            errorMessage ||
            'This feature is unavailable. Please check your subscription status, limits, and plan features.';

          confirmationService.confirm({
            header: `SUBSCRIPTION ERROR`,
            message: subscriptionErrorMessage,
            icon: 'pi pi-exclamation-circle',
            acceptLabel: 'View Plans',
            acceptIcon: 'pi',
            acceptButtonStyleClass: 'p-button-primary',
            rejectVisible: false,
            accept: () => {
              router.navigateByUrl('/subscription/plans');
            },
          });
        }
      } else if (error.status === 401) {
        console.error(errorMessage);
      } else {
        notificationService.openErrorToast(errorMessage);
      }
      return throwError(() => error);
    }),
  );
};
